import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchLeagueBySlug, LeagueDetail } from '../lib/api';
import FixtureCard from '../components/fixtures/FixtureCard';
import EmptyState from '../components/ui/EmptyState';
import { HeroSkeleton, TeamCardSkeleton, FixtureCardSkeleton } from '../components/ui/Skeleton';
import usePageTitle from '../hooks/usePageTitle';
import {
  ChevronRightIcon,
  UserGroupIcon,
  CalendarIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';

export default function LeaguePage() {
  const { slug } = useParams<{ slug: string }>();
  const [data, setData] = useState<LeagueDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  usePageTitle(data?.league.name);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetchLeagueBySlug(slug)
      .then(setData)
      .catch(() => setError('League not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div>
        <div className="h-4 w-48 mb-6" />
        <HeroSkeleton />
        <div className="mb-10">
          <div className="h-5 w-20 mb-4 rounded bg-slate-700/50 animate-pulse" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {Array.from({ length: 10 }).map((_, i) => <TeamCardSkeleton key={i} />)}
          </div>
        </div>
        <div className="h-5 w-36 mb-4 rounded bg-slate-700/50 animate-pulse" />
        <div className="grid gap-3">
          {Array.from({ length: 4 }).map((_, i) => <FixtureCardSkeleton key={i} />)}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <EmptyState
        icon={<TrophyIcon className="w-7 h-7" />}
        title="League not found"
        description="We couldn't find the league you're looking for."
        actionLabel="Browse Leagues"
        actionTo="/leagues"
      />
    );
  }

  const { league, teams, fixtures } = data;

  return (
    <div>
      {/* Breadcrumbs */}
      <nav className="flex items-center gap-1.5 text-sm text-slate-500 mb-6">
        <Link to="/" className="hover:text-slate-300 transition-colors">Home</Link>
        <ChevronRightIcon className="w-3 h-3" />
        <Link to="/leagues" className="hover:text-slate-300 transition-colors">Leagues</Link>
        <ChevronRightIcon className="w-3 h-3" />
        <span className="text-slate-300">{league.name}</span>
      </nav>

      {/* League hero */}
      <div className="card p-6 sm:p-8 mb-8">
        <div className="flex items-center gap-5">
          {league.logo && (
            <img
              src={league.logo}
              alt={league.name}
              className="w-16 h-16 rounded-xl bg-slate-700 object-contain p-2"
            />
          )}
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-100">{league.name}</h1>
            <div className="flex items-center gap-4 mt-2 text-sm text-slate-400">
              <span className="flex items-center gap-1.5">
                <UserGroupIcon className="w-4 h-4" />
                {teams.length} teams
              </span>
              <span className="flex items-center gap-1.5">
                <CalendarIcon className="w-4 h-4" />
                {fixtures.length} upcoming fixture{fixtures.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Teams grid */}
      <section className="mb-10">
        <h2 className="text-lg font-semibold text-slate-200 mb-4">Teams</h2>
        {teams.length === 0 ? (
          <p className="text-slate-400 text-sm">No teams found for this league.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {teams.map((team) => (
              <Link
                key={team.id}
                to={`/team/${team.slug}`}
                className="card p-4 flex flex-col items-center gap-3 hover:bg-slate-700/30 transition-all group"
              >
                {team.logo ? (
                  <img
                    src={team.logo}
                    alt={team.name}
                    className="w-12 h-12 rounded-lg object-contain"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-slate-700 flex items-center justify-center text-slate-400 text-lg font-bold">
                    {team.name.charAt(0)}
                  </div>
                )}
                <span className="text-sm text-slate-300 text-center font-medium group-hover:text-slate-100 transition-colors leading-tight">
                  {team.name}
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Upcoming fixtures */}
      <section>
        <h2 className="text-lg font-semibold text-slate-200 mb-4">Upcoming Fixtures</h2>
        {fixtures.length === 0 ? (
          <EmptyState
            icon={<CalendarIcon className="w-7 h-7" />}
            title="No upcoming fixtures"
            description="No upcoming fixtures with price comparisons in this league."
          />
        ) : (
          <div className="grid gap-3">
            {fixtures.map((fixture) => (
              <FixtureCard
                key={fixture.match_id}
                fixture={fixture}
                showLeague={false}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
